import { HeroCarousel } from '@components/HeroCarousel'
import { YStack, Text, XStack, Stack, useMedia, Circle, isWeb } from 'tamagui'
import { CoverImage } from '@components/CoverImage'
import { useTranslation } from 'react-i18next'
import { ICourseContentsNodes, ILesson, ILessons } from '@dtos/Course'
import { HeroIcon } from '@components/HeroIcon'
import { formatDateShort, isReleaseDayReached } from '@utils/dateFormatter'
import { LessonTypeTranslation } from '@utils/lessonMediaType'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { IEnrollment } from '@dtos/Enrollment'
import { useCourse } from '@hooks/useCourse'
import { useModules } from '@hooks/useModules'
import { useExtraContents } from '@hooks/useExtraContents'
import { CourseDescription } from '../CourseDescription'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { contentReleaseDate, isContentAvailable } from '@utils/contentAvailable'
import { getVideoThumb } from '@utils/videoThumbs'
import { LastViewedLessonSlide } from '../LastViewedLessonSlide'
import { CourseActionButtons } from './CourseActionButtons'
import { useMediaSize } from '@hooks/useMediaSize'
import { openWebBrowser } from '@utils/openWebBrowser'
import { Progress } from '@components/Progress'
import { ContentReportButton } from '@components/ContentReport/ContentReportButton'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { getAccessDays } from '@utils/accessDays'
import { useHasFeature } from '@hooks/useThemeParams'
import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import {
  courseContentPath,
  extraContentsPath,
  heroThemeCourseContentPath,
} from '@utils/coursePaths'
import { useAdminPreview } from '@hooks/useAdminPreview'
import {
  checkProgress,
  isCourseCompleted,
  progressColor,
} from '@utils/courseProgress'

const SLIDE_DIMENSIONS = {
  xs: { height: 141, width: 256 },
  sm: { height: 141, width: 256 },
  md: { height: 296, width: 490 },
  lg: { height: 400, width: 680 },
  xxl: { height: 400, width: 680 },
} as const

interface INextReleaseLesson {
  content: ICourseContentsNodes
  releaseAt: string
}

interface IFirstAvailableLesson {
  moduleId?: number
  id: number
  available: boolean | null
  courseContentsCount: number
  lesson: ILesson
}

interface ICourseDetailsProps extends IEnrollment {
  lastLessonsViewedContent: ILesson[] | undefined
}

export function CourseDetails({
  enrollment,
  lessonsExtraContents,
}: {
  enrollment: ICourseDetailsProps
  lessonsExtraContents: ILessons | undefined
}) {
  const { course } = useCourse()
  const { modules } = useModules()
  const { extraContents } = useExtraContents()
  const { courseId, enrollmentId } = useLocalSearchParams()
  const { setCurrentLesson } = useCurrentLesson()
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const { isPreview } = useAdminPreview()

  const { t } = useTranslation('CourseDetails')
  const router = useRouter()
  const media = useMedia()
  const size = useMediaSize()
  const textTransform = isWeb ? 'translate(-49%, 0)' : 'unset'
  const lastLessonViewedMarginTop = media.gtSm ? 0 : 74
  const courseDetailsMarginBottom = media.gtSm ? 100 : 44
  const [isLoadingExtraContent, setIsLoadingExtraContent] = useState(true)

  const courseNameFontSize = useMemo(() => {
    if (media.gtLg) return '$font-size-xxl'
    if (media.gtSm) return '$font-size-lg'
    return '$font-size-md'
  }, [media.gtLg, media.gtSm])

  const firstAvailableLessonFontSize = useMemo(() => {
    if (media.gtXl) return '$font-size-lg'
    if (media.gtSm) return '$font-size-md'
    return '$font-size-xs'
  }, [media.gtXl, media.gtSm])

  const handleExtraContentRedirect = useCallback(() => {
    router.push({
      pathname: extraContentsPath({ isPreview, enrollmentId, courseId }),
      params: { courseName: course?.name },
    })
  }, [router, enrollmentId, courseId, course?.name, isPreview])

  const handleLessonAccess = useCallback(
    (lessonData: IFirstAvailableLesson) => {
      setCurrentLesson(lessonData.lesson)

      // Escolas sem a feature members_page_course_contents não têm a página
      // de consumo no SPA de membros — navegar via router deixaria a URL 404
      // no refresh. No web, redireciona (full load) para a página legada do
      // hero_theme (sem /online_course). No nativo, sempre usa o router.
      if (isWeb && !isPreview && !hasCourseContentsPage) {
        window.location.href = heroThemeCourseContentPath({
          enrollmentId: enrollment.id,
          courseId,
          courseContentId: lessonData.lesson.courseContentId,
        })
        return
      }

      const formattedPath = courseContentPath({
        isPreview,
        enrollmentId: enrollment.id,
        courseId,
        courseContentId: lessonData.lesson.courseContentId,
        moduleId: lessonData.moduleId,
      })
      router.navigate(formattedPath)
    },
    [
      enrollment.id,
      courseId,
      setCurrentLesson,
      router,
      hasCourseContentsPage,
      isPreview,
    ],
  )

  const courseCompleted: boolean = useMemo(
    () => isCourseCompleted(enrollment),
    [enrollment],
  )

  const certificateButtonVariant = courseCompleted ? 'secondary' : 'disabled'

  const accessDays = useMemo(() => {
    return getAccessDays({
      expireDate: enrollment.schoolProduct.expireDate,
      activatedAt: enrollment.activatedAt,
      availableTimeLength: enrollment.schoolProduct.availableTimeLength,
      availableUntil: enrollment.availableUntil,
      unlimited: enrollment.unlimited,
    })
  }, [
    enrollment.schoolProduct.expireDate,
    enrollment.schoolProduct.availableTimeLength,
    enrollment.activatedAt,
    enrollment.availableUntil,
    enrollment.unlimited,
  ])

  const badgeStyle =
    accessDays && accessDays.days <= 7 ? '$danger-300' : '$info-300'
  const accessDaysLabel =
    accessDays && accessDays.days <= 7
      ? 'Atenção! acesso acaba em:'
      : 'Acesso liberado até:'

  const checkIfContentIsAvailable = useCallback(
    (lesson: ILesson) => {
      const dataAvailable = {
        releaseAt: lesson.releaseAt,
        releaseAfter: lesson.releaseAfter,
        lockAt: lesson.lockAt,
        lockAfter: lesson.lockAfter,
        createdAt: enrollment.activatedAt,
        courseClassRelease: enrollment.courseClassRelease,
        courseClassReleaseOffset: lesson.courseClassReleaseOffset,
      }
      return isContentAvailable(dataAvailable)
    },
    [enrollment.activatedAt, enrollment.courseClassRelease],
  )

  const firstAvailableLesson: IFirstAvailableLesson | null = useMemo(() => {
    if (!modules) return null
    const getAvailableLessons = (contents: ICourseContentsNodes[]) =>
      contents.filter(
        (content) =>
          content.available !== false &&
          checkIfContentIsAvailable(content.lesson),
      )

    for (const module of modules.nodes) {
      if (module.available === false) continue

      const availableLessons = getAvailableLessons(module.courseContents.nodes)
      if (availableLessons.length)
        return {
          ...availableLessons[0],
          moduleId: module.id,
        }

      for (const submodule of module.courseModules) {
        const availableSubLessons = getAvailableLessons(
          submodule.courseContents.nodes,
        )
        if (availableSubLessons.length)
          return {
            ...availableSubLessons[0],
            moduleId: submodule.id,
          }
      }
    }

    return null
  }, [modules, checkIfContentIsAvailable])

  // Conteúdo que libera primeiro entre os ainda bloqueados. Sustenta o estado
  // do header quando NADA está disponível — comum em curso com turma, entre a
  // matrícula e a data de início.
  const nextReleaseLesson = useMemo(() => {
    if (!modules) return null

    const releaseDateFor = (lesson: ILesson) =>
      contentReleaseDate({
        releaseAt: lesson.releaseAt,
        releaseAfter: lesson.releaseAfter,
        lockAt: lesson.lockAt,
        lockAfter: lesson.lockAfter,
        createdAt: enrollment.activatedAt,
        courseClassRelease: enrollment.courseClassRelease,
        courseClassReleaseOffset: lesson.courseClassReleaseOffset,
      })

    let earliest: INextReleaseLesson | null = null

    const consider = (contents: ICourseContentsNodes[]) => {
      contents.forEach((content) => {
        if (checkIfContentIsAvailable(content.lesson)) return
        const releaseAt = releaseDateFor(content.lesson)
        if (!releaseAt) return
        // Conteúdo indisponível por bloqueio (lockAt/lockAfter) tem data de
        // liberação no passado. Sem esta guarda o header anunciava
        // "Disponível em" com uma data que já passou.
        //
        // O corte é por dia, igual ao de isContentAvailable: com isPastDate, um
        // conteúdo que libera hoje mais tarde e está travado por lockAt entrava
        // aqui e o header prometia "Disponível em <hoje>".
        if (isReleaseDayReached(releaseAt)) return
        if (!earliest || new Date(releaseAt) < new Date(earliest.releaseAt)) {
          earliest = { content, releaseAt }
        }
      })
    }

    modules.nodes.forEach((module) => {
      if (module.available === false) return
      consider(module.courseContents.nodes)
      module.courseModules.forEach((submodule) =>
        consider(submodule.courseContents.nodes),
      )
    })

    return earliest as INextReleaseLesson | null
  }, [
    modules,
    checkIfContentIsAvailable,
    enrollment.activatedAt,
    enrollment.courseClassRelease,
  ])

  // Só afirmamos algo quando há uma liberação futura de fato. Sem isso o
  // conteúdo pode estar indisponível por expiração (lockAt/availableUntil), e
  // "ainda não liberado" seria uma afirmação errada — nesse caso o botão não
  // aparece, já que não existe ação possível.
  const blockedLabel = nextReleaseLesson
    ? `Disponível em ${formatDateShort(nextReleaseLesson.releaseAt)}`
    : null

  // Sem conteúdo liberado, mostra o próximo a liberar apenas como prévia —
  // sem clique, como aparece bloqueado na ementa.
  const previewLesson = firstAvailableLesson ?? nextReleaseLesson?.content
  const isPreviewClickable = Boolean(firstAvailableLesson)

  const lastViewedLessonsNumber =
    enrollment.lastLessonsViewedContent?.length || 0
  const isYoutubeOrVimeo = ['Youtube', 'Vimeo'].includes(
    previewLesson?.lesson.media?.host || '',
  )
  const displayThumbnail =
    previewLesson?.lesson.coverImage?.cdnFileUrl || isYoutubeOrVimeo
  const lastViewedSlideSizes = useMemo(() => {
    return SLIDE_DIMENSIONS[size] || SLIDE_DIMENSIONS.xs
  }, [size])

  const hasExtraContent: boolean = useMemo(() => {
    if (
      !lessonsExtraContents ||
      !extraContents ||
      !Object.keys(extraContents).length
    )
      return false
    const lessonsExtraContent = lessonsExtraContents.nodes.find(
      (lesson: ILesson) => {
        if (!lesson.extraMedias) return false
        const dataAvailable = {
          releaseAt: lesson.releaseAt,
          releaseAfter: lesson.releaseAfter,
          lockAt: lesson.lockAt,
          lockAfter: lesson.lockAfter,
          createdAt: enrollment.activatedAt,
          courseClassRelease: enrollment.courseClassRelease,
          courseClassReleaseOffset: lesson.courseClassReleaseOffset,
        }
        return isContentAvailable(dataAvailable)
      },
    )
    return extraContents.nodes.length > 0 || !!lessonsExtraContent
  }, [
    lessonsExtraContents,
    extraContents,
    enrollment.activatedAt,
    enrollment.courseClassRelease,
  ])

  const buttonConfig = useMemo(() => {
    if (
      enrollment.lastLessonsViewedContent &&
      enrollment.lastLessonsViewedContent.length
    ) {
      const id = enrollment.lastLessonsViewedContent[0].courseContentId
      const lessonData: IFirstAvailableLesson = {
        lesson: enrollment.lastLessonsViewedContent[0],
        id: Number(id),
        available: isContentAvailable({
          ...enrollment.lastLessonsViewedContent[0],
          courseClassRelease: enrollment.courseClassRelease,
        }),
        courseContentsCount: 1,
      }
      return {
        label: 'Continuar',
        onPress: () => handleLessonAccess(lessonData),
      }
    }
    if (firstAvailableLesson) {
      return {
        label: 'Iniciar',
        onPress: () => handleLessonAccess(firstAvailableLesson),
      }
    }
  }, [
    enrollment.lastLessonsViewedContent,
    enrollment.courseClassRelease,
    firstAvailableLesson,
    handleLessonAccess,
  ])

  const handleButtonPress = useCallback(() => {
    buttonConfig?.onPress()
  }, [buttonConfig])

  const handleFirstLessonAccess = useCallback(() => {
    if (firstAvailableLesson) handleLessonAccess(firstAvailableLesson)
  }, [firstAvailableLesson, handleLessonAccess])

  useEffect(() => {
    if (lessonsExtraContents && extraContents) {
      setIsLoadingExtraContent(false)
    }
  }, [lessonsExtraContents, extraContents])

  const handleCertificatePress = useCallback(() => {
    if (!enrollment.certificates?.length) return
    openWebBrowser(enrollment.certificates[0]?.fileUrl)
  }, [enrollment.certificates])

  if (!course || !modules) return null

  const marginWithoutDescription = media.gtSm && !course.description ? 40 : 0

  return (
    <Stack
      flexDirection="column"
      marginBottom={marginWithoutDescription}
      paddingHorizontal={'$size.spacing-xs'}
      mt={0}
      mb={courseDetailsMarginBottom}
      $gtSm={{
        paddingHorizontal: '$size.spacing-xs',
      }}
      $gtMd={{
        marginTop: '$spacing-inset-lg',
        paddingHorizontal: '$size.spacing-xsl',
      }}
      $gtLg={{
        width: '100%',
        maxWidth: 1608,
        marginHorizontal: 'auto',
        paddingHorizontal: '$size.spacing-lg',
      }}
    >
      <Stack
        $gtSm={{
          flexDirection: 'row',
        }}
      >
        <YStack
          marginBottom={marginWithoutDescription}
          $gtSm={{ width: '50%' }}
          $gtMd={{ alignSelf: 'baseline' }}
        >
          <XStack alignItems="flex-start">
            <YStack flex={1}>
              {course.kind === 'online_course' && (
                <Text
                  color="$text-body"
                  ff="$mulishRegular"
                  fontSize="$font-size-xxxs"
                  marginBottom="$spacing-inset-xxs"
                >
                  {t(`product_type.${course.kind}`)}
                </Text>
              )}
              {accessDays && (
                <XStack
                  borderRadius="$border-radius-pill"
                  padding="$spacing-inset-nano"
                  marginBottom="$spacing-inset-xxs"
                  bg="$bg-surface-raised"
                  alignItems="center"
                  alignSelf="flex-start"
                >
                  <Circle bg={badgeStyle} size="$spacing-md" marginRight={8}>
                    <HeroIcon
                      icon={['fal', 'calendar-alt']}
                      size={16}
                      color="$text-on-accent"
                    />
                  </Circle>
                  <Text
                    color="$text-primary"
                    ff="$mulishBold"
                    fontSize="$font-size-xs"
                    marginRight="$spacing-inset-quarck"
                  >
                    {accessDaysLabel}
                  </Text>
                  <Text
                    color={badgeStyle}
                    ff="$mulishBold"
                    fontSize="$font-size-xs"
                  >
                    {formatDateShort(accessDays.expireDate)}
                  </Text>
                </XStack>
              )}
              <Text
                color="$text-primary"
                ff="$mulishBold"
                fontSize={courseNameFontSize}
                marginBottom="$spacing-inset-sm"
              >
                {course.name}
              </Text>
            </YStack>
            <ContentReportButton
              schoolProductId={String(enrollment.schoolProduct.id)}
              courseId={String(courseId)}
            />
          </XStack>
          {enrollment.progressCard && (
            <YStack>
              <Progress
                value={enrollment.progressCard.progress}
                height={8}
                foregroundColor={progressColor(
                  enrollment.progressCard.progress,
                )}
              />
              <XStack
                marginTop="$spacing-inset-nano"
                flex={1}
                justifyContent="space-between"
              >
                <XStack gap="$spacing-inset-quarck">
                  <Text
                    color="$progress-complete"
                    ff="$mulishBold"
                    fontSize="$font-size-xs"
                  >
                    {enrollment.progressCard.completed}
                  </Text>
                  <Text
                    color="$text-secondary"
                    ff="$mulishBold"
                    fontSize="$font-size-xs"
                  >
                    de {enrollment.progressCard.total} conteúdos
                  </Text>
                </XStack>
                {enrollment.progressCard.progress > 0 && (
                  <XStack gap="$spacing-inset-quarck">
                    <Text
                      color="$progress-complete"
                      ff="$mulishBold"
                      fontSize="$font-size-xs"
                    >
                      {checkProgress(enrollment.progressCard.progress)}%
                    </Text>
                    <Text
                      color="$text-secondary"
                      ff="$mulishBold"
                      fontSize="$font-size-xs"
                    >
                      concluído
                    </Text>
                  </XStack>
                )}
              </XStack>
            </YStack>
          )}
          <CourseActionButtons
            buttonConfig={buttonConfig}
            blockedLabel={blockedLabel}
            handleButtonPress={handleButtonPress}
            progress={enrollment.progressCard?.progress}
            hasExtraContent={hasExtraContent}
            isLoadingExtraContent={isLoadingExtraContent}
            handleExtraContentRedirect={handleExtraContentRedirect}
            hasCertification={!!enrollment?.schoolProduct.certification}
            courseCompleted={courseCompleted}
            certificateButtonVariant={certificateButtonVariant}
            handleCertificatePress={handleCertificatePress}
            certificationMinProgress={
              enrollment.schoolProduct.certificationMinProgress
            }
          />
          {!isWeb &&
            enrollment.schoolProduct.certification &&
            courseCompleted === false && (
              <XStack
                marginTop="$spacing-inset-xxs"
                gap="$spacing-inset-nano"
                alignItems="center"
              >
                <HeroIcon
                  icon={['fal', 'info-circle']}
                  size={16}
                  color="$icon-secondary"
                />
                <Text
                  color="$text-secondary"
                  ff="$mulishBold"
                  fontSize="$font-size-xxxs"
                  textAlign="center"
                  numberOfLines={2}
                >
                  {`Complete ${enrollment.schoolProduct.certificationMinProgress}% de progresso para receber o certificado`}
                </Text>
              </XStack>
            )}
          {(!media.gtSm || media.gtMd) && course.description && (
            <CourseDescription description={course.description} />
          )}
        </YStack>
        <YStack
          marginTop={lastLessonViewedMarginTop}
          flex={1}
          $gtSm={{
            alignSelf: 'flex-start',
            paddingLeft: '$spacing-inset-xxs',
          }}
        >
          {enrollment.lastLessonsViewedContent?.length ? (
            <>
              <HeroCarousel
                height={lastViewedSlideSizes.height}
                numberOfSlides={lastViewedLessonsNumber}
                displayControls={!!isWeb}
                isCarousel={true}
                sliderTitle={
                  <Text
                    width={lastViewedSlideSizes.width}
                    left="10%"
                    transform={textTransform}
                    position="absolute"
                    marginBottom="$spacing-inset-xs"
                    fontSize="$font-size-xs"
                    ff="$mulishSemiBold"
                    top={-30}
                    color="$text-secondary"
                    $gtSm={{ left: '50%' }}
                    $md={{
                      left: '20%',
                    }}
                  >
                    Visto por último
                  </Text>
                }
              >
                {enrollment.lastLessonsViewedContent.map((item, index) => (
                  <LastViewedLessonSlide
                    key={item.id}
                    item={item}
                    width={lastViewedSlideSizes.width}
                    height={lastViewedSlideSizes.height}
                    idx={index}
                    isClickable={true}
                    onPressSlide={() =>
                      handleLessonAccess({
                        lesson: item,
                        id: item.id,
                        available: isContentAvailable({
                          ...item,
                          courseClassRelease: enrollment.courseClassRelease,
                        }),
                        courseContentsCount: 1,
                      })
                    }
                  />
                ))}
              </HeroCarousel>
            </>
          ) : previewLesson ? (
            <YStack paddingHorizontal="$spacing-inset-lg">
              <Text
                fontSize="$font-size-xxs"
                ff="$mulishBold"
                color="$text-secondary"
                marginBottom="$spacing-inset-md"
              >
                {isPreviewClickable ? 'Comece por aqui:' : 'Próximo conteúdo:'}
              </Text>
              {displayThumbnail ? (
                <Stack
                  style={isPreviewClickable ? { cursor: 'pointer' } : undefined}
                  onPress={
                    isPreviewClickable ? handleFirstLessonAccess : undefined
                  }
                  opacity={isPreviewClickable ? 1 : 0.5}
                  width={256}
                >
                  <CoverImage
                    uri={
                      getVideoThumb({
                        ...previewLesson.lesson.media,
                        coverImage: previewLesson.lesson.coverImage,
                      }) || undefined
                    }
                    width={256}
                    height={141}
                    borderRadius={8}
                    alt="Imagem do curso"
                  />
                </Stack>
              ) : (
                <XStack
                  bg="$bg-surface-sunken"
                  alignItems="center"
                  borderRadius="$border-radius-md"
                  justifyContent="center"
                  style={isPreviewClickable ? { cursor: 'pointer' } : undefined}
                  onPress={
                    isPreviewClickable ? handleFirstLessonAccess : undefined
                  }
                  opacity={isPreviewClickable ? 1 : 0.5}
                  height={lastViewedSlideSizes.height}
                >
                  <HeroIcon
                    icon={LessonTypeTranslation(
                      previewLesson.lesson.activity !== null
                        ? 'Quiz'
                        : previewLesson.lesson.media?.type || 'Video',
                    )}
                    size={40}
                    color="$accent"
                  />
                </XStack>
              )}

              <Text
                marginTop="$spacing-inset-xs"
                fontSize={firstAvailableLessonFontSize}
                color="$text-primary"
                ff="$mulishSemiBold"
              >
                {previewLesson.lesson.title}
              </Text>
            </YStack>
          ) : null}
        </YStack>
      </Stack>
    </Stack>
  )
}
